import React, { useCallback, useState } from 'react';

import {
  Button,
  Heading,
  HStack,
  Skeleton,
  Text,
  VStack,
} from '@rtkwlf/fenrir-react';
import { ScannerConnectionStatus, type ScannerDetails } from '@rtkwlf/rendall';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { useScannerDetails } from '@apis/rendall/queries/use-scanner-details/use-scanner-details';

import { CredentialedScanningTable } from './components/credentialed-scanning-table/credentialed-scanning-table';
import { ScanCredentialsDialog } from './components/scan-credentials-dialog/scan-credentials-dialog';
import { useScannerScanCredentialsWrapper } from './components/use-scanner-scan-credentials-wrapper/use-scanner-scan-credentials-wrapper';

export const CredentialedScanning: React.FC = () => {
  const { t } = useTranslation('telemetryScannerConfig', {
    keyPrefix: 'credentialedScanning',
  });
  const { scannerId = '' } = useParams();
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const { isFetching } = useScannerScanCredentialsWrapper(scannerId);
  const { data: isConnected } = useScannerDetails(scannerId, {
    select: useCallback(
      ({ connectionStatus }: ScannerDetails) =>
        connectionStatus === ScannerConnectionStatus.Connected,
      [],
    ),
  });

  return (
    <VStack width="full" gap="smedium">
      <VStack gap="smedium" width="full" marginBottom="large">
        <HStack width="full" justifyContent="space-between">
          <VStack gap="smedium">
            <Heading level="4" paddingY="xsmall">
              {t('title')}
            </Heading>
            <Text styledAs="body2">{t('subtitle')}</Text>
          </VStack>
          {isFetching ? (
            <Skeleton
              width="full"
              maxWidth="14rem"
              margin="zero"
              data-testid="create-credential-skeleton"
            />
          ) : (
            <Button
              variant="secondary"
              onClick={() => setShowDialog(true)}
              isDisabled={!isConnected}
            >
              {t('createNewScanCredentialsButton')}
            </Button>
          )}
        </HStack>
      </VStack>

      <CredentialedScanningTable />
      {showDialog && (
        <ScanCredentialsDialog
          scannerId={scannerId}
          onClose={() => {
            setShowDialog(false);
          }}
        />
      )}
    </VStack>
  );
};
